import { Fragment } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { AutoAwesome, Close as CloseIcon, Palette, Public, Storefront } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { RightPanelOverlayModal } from 'app/shared-components/modals';
import { PrimaryButton, TransparentButton } from 'app/shared-components/buttons';
import FeatureCard from 'app/shared-components/FeatureCard';
import useEventTracker from 'app/hooks/useEventTracker';
import useOrganisationFeatures from 'app/hooks/useOrganisationFeatures';
import useSelectedPharmacy from 'app/hooks/useSelectedPharmacy';
import { UnlockWithPlusChip } from 'app/shared-components/PlusPaywall';
import { WEBSITE_GUIDE_EVENTS } from 'app/tracking/constants';

import HubSpotVideo from './HubSpotVideo';

const WEBSITE_BUILDER_PATH = '/plus-websites';
const ONBOARDING_PATH = `${WEBSITE_BUILDER_PATH}/onboarding`;

const FEATURES = [
  {
    icon: Palette,
    title: 'Pick a Look',
    description: 'Choose a theme, add your logo and brand colours in a few clicks.',
  },
  {
    icon: Storefront,
    title: 'Add Your Services',
    description: 'Services and products flow straight from Connect, always up to date.',
  },
  {
    icon: Public,
    title: 'Go Live',
    description: 'Publish on your own domain in minutes. No developer needed.',
  },
];

interface WebBuilderPromoModalProps {
  /** Closing it in any way ("Not now", the close button or the CTA) counts as dismissed. */
  onDismiss: () => void;
}

/**
 * "What's new" modal introducing the Website builder with its guide video. When to show it is
 * `useWebBuilderPromoPopUp`'s call.
 */
export default function WebBuilderPromoModal({
  onDismiss,
}: WebBuilderPromoModalProps): JSX.Element {
  const navigate = useNavigate();
  const trackEvent = useEventTracker();
  const { pricingLaunched } = useOrganisationFeatures();
  const organisationId = useSelectedPharmacy()?.chain_id;

  const handleDismiss = () => {
    trackEvent(WEBSITE_GUIDE_EVENTS.PROMO_DISMISSED.action, WEBSITE_GUIDE_EVENTS.PROMO_DISMISSED);
    onDismiss();
  };

  const handleTryIt = () => {
    trackEvent(
      WEBSITE_GUIDE_EVENTS.PROMO_CTA_CLICKED.action,
      WEBSITE_GUIDE_EVENTS.PROMO_CTA_CLICKED
    );
    onDismiss();
    // Without a selected organisation the entry route lets the organisation selector pick one.
    navigate(
      organisationId
        ? { pathname: ONBOARDING_PATH, search: `?organisationId=${organisationId}` }
        : WEBSITE_BUILDER_PATH
    );
  };

  return (
    <RightPanelOverlayModal open onClose={handleDismiss}>
      <Box className="vtwo:bg-white vtwo:rounded-xl vtwo:flex vtwo:flex-col vtwo:w-[680px] vtwo:px-5 vtwo:py-4 vtwo:shadow-lg vtwo:outline-none">
        <Box className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:mb-4">
          <Box className="vtwo:flex vtwo:items-center vtwo:gap-3">
            <Typography className="vtwo:font-bold vtwo:text-lg vtwo:text-typography-primary">
              What's new
            </Typography>
            {pricingLaunched && <UnlockWithPlusChip />}
          </Box>
          <IconButton
            onClick={handleDismiss}
            size="small"
            className="vtwo:text-neutral-500"
            aria-label="Close Website builder promotion"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography className="vtwo:font-bold vtwo:text-xl vtwo:text-typography-primary vtwo:text-center vtwo:mb-2">
          Build Your Pharmacy Website in Minutes
        </Typography>

        <Typography className="vtwo:text-sm vtwo:text-typography-secondary vtwo:text-center vtwo:mb-4 vtwo:leading-[22px]">
          Design, publish and update your website straight from Connect.
        </Typography>

        <Box className="vtwo:rounded-xl vtwo:overflow-hidden vtwo:bg-neutral-100 vtwo:mx-auto vtwo:mb-5 vtwo:w-full vtwo:max-w-[520px]">
          <HubSpotVideo />
        </Box>

        <Box className="vtwo:flex vtwo:gap-4 vtwo:mb-6">
          {FEATURES.map((feature, index) => (
            <Fragment key={feature.title}>
              <FeatureCard
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
              {index < FEATURES.length - 1 && <Divider orientation="vertical" flexItem />}
            </Fragment>
          ))}
        </Box>

        <Box className="vtwo:flex vtwo:items-center vtwo:justify-end vtwo:gap-3">
          <TransparentButton label="Not now" cb={handleDismiss} size="m" outlined />
          <PrimaryButton
            label="Try Website builder"
            icon={<AutoAwesome />}
            cb={handleTryIt}
            size="m"
            variant="featured"
          />
        </Box>
      </Box>
    </RightPanelOverlayModal>
  );
}
